import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import CircularProgressLoader from '../../components/CircularProgressLoader';

const FULL_SIZE_IFRAME_STYLE = { width: '100%', height: '100%', flex: 1, alignSelf: 'stretch', border: 'none' };
const TEXT_CONTENT_STYLE = { whiteSpace: 'pre-line', wordBreak: 'break-word', padding: 2 };
// Brand HTML is authored with fixed/narrow containers (width: 80%, max-width: 600px, width: 100% plus
// padding without border-box) and small 14px text. Force it to fill the iframe width without
// horizontal scroll, and bump the text size (CSS zoom is avoided: many mobile browsers resolve
// percentage widths before zooming, which pushes content past the screen edge). Appended (not
// prepended) so it doesn't push the doctype down and trigger quirks mode, and so it wins the cascade.
const HTML_FIT_STYLE = `<style>
    *, *::before, *::after { box-sizing: border-box; }
    html, body { width: 100% !important; max-width: 100% !important; margin: 0 !important; overflow-x: hidden; }
    body > * { width: 100% !important; max-width: 100% !important; margin-left: 0 !important; margin-right: 0 !important; padding-left: 16px !important; padding-right: 16px !important; }
    img, video, iframe, table { max-width: 100% !important; height: auto; }
    body { overflow-wrap: break-word; }
    body :is(p, span, li, a, strong, td, th, div) { font-size: 16px !important; line-height: 1.5 !important; }
    body :is(h1, h2, h3, h4, h5, h6) { font-size: 18px !important; }
</style>`;

const isLink = (value) => typeof value === 'string' && /^https?:\/\//i.test(value.trim());
const isHtml = (value) => typeof value === 'string' && /<\/?[a-z][\s\S]*>/i.test(value);

// Generic viewer for a drawer menu item's "value": a direct URL is used as-is, otherwise the
// value is treated as a translation key. The resolved content renders as an iframe when it's a
// URL, as a sandboxed iframe document when it's HTML (brand HTML ships full documents with their
// own <style>, which would leak into the app if injected inline), otherwise as plain text.
// i18n is initialised with returnObjects: true, so t() may hand back an array (one entry per
// paragraph) instead of a string.
const DrawerMenuContentPage = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const { content: contentKey, title } = location.state || {};
    const content = isLink(contentKey) ? contentKey.trim() : t(contentKey);
    const isUrl = isLink(content);
    const isHtmlContent = !isUrl && isHtml(content);
    const paragraphs = (Array.isArray(content) ? content : [content]).filter((item) => typeof item === 'string' && item.trim());
    const [loading, setLoading] = useState(true);
    const hideLoaderOnLoad = () => setLoading(false);
    console.log("isUrl", isUrl, "content", content);

    useEffect(() => {
        if (!isUrl) {
            setLoading(false);
        }
    }, [isUrl]);

    return (
        <Box className="drawer-menu-content-page">
            <Box className="main-container">
                <Box className="main">
                    {loading && <CircularProgressLoader message={t('pleaseWaitText')} />}
                    {isUrl ? (
                        <iframe src={content.trim()} title={t(title)} style={FULL_SIZE_IFRAME_STYLE} onLoad={hideLoaderOnLoad} />
                    ) : isHtmlContent ? (
                        <iframe srcDoc={content + HTML_FIT_STYLE} title={t(title)} style={FULL_SIZE_IFRAME_STYLE} sandbox="allow-popups allow-popups-to-escape-sandbox" />
                    ) : (
                        <Box sx={TEXT_CONTENT_STYLE}>
                            {paragraphs.map((paragraph, index) => (
                                <Typography key={index} variant="body1" paragraph>{paragraph}</Typography>
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default DrawerMenuContentPage;
